import { createFileRoute, Link } from "@tanstack/react-router";
import type { JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import IllustrationConfetti from "@repo/icons/colored/IllustrationConfetti";
import { CpsSpinner } from "corpus";

import { Button } from "@/components/ui/button/button";
import { ImageDrawerFlowLayout } from "@/components/ui/layout/image-drawer-flow-layout";
import { useFirstScheduleIntroDestination } from "@/hooks/use-first-schedule-intro-destination";
import { useGraphQL } from "@/hooks/use-graphql";
import { FLOW_IMAGE_MAP } from "@/lib/maps/flow-image-map";

const FlowCompleteQuery = graphql(/* GraphQL */ `
  query FlowCompleteQuery($codFluxo: Int!) {
    membro {
      fluxos(where: { codFluxo: { eq: $codFluxo } }) {
        codFluxo
        configuracoes
      }
    }
  }
`);

const FlowCompletePage = (): JSX.Element => {
  const { flowId } = Route.useParams();
  const { destination, trackContinue } = useFirstScheduleIntroDestination({
    origin: "fim_do_fluxo",
    flowId: Number(flowId),
  });

  const { data, isLoading } = useGraphQL(FlowCompleteQuery, {
    codFluxo: Number(flowId),
  });

  const flow = data?.membro?.fluxos?.[0];

  const displayTitle = flow?.configuracoes?.fimFluxoTitulo ?? "Obrigado!";
  const displayDescription =
    flow?.configuracoes?.fimFluxoDescricao ??
    "Suas informações foram salvas com sucesso.";
  const displayButtonText = flow?.configuracoes?.fimFluxoBotao ?? "Continuar";
  const imageKey = flow?.configuracoes?.fimFluxoImagem ?? "old-doctor-with-patient";
  const image = FLOW_IMAGE_MAP[imageKey] ?? FLOW_IMAGE_MAP["old-doctor-with-patient"];

  if (isLoading) {
    return <CpsSpinner />;
  }

  return (
    <ImageDrawerFlowLayout
      imageSrc={image}
      imageAlt="Profissional de saúde recebendo paciente na Livance"
    >
      <div className="flex flex-col gap-2">
        <IllustrationConfetti size={80} aria-hidden="true" />
        <h1 className="whitespace-pre-line text-2xl font-medium leading-[1.32] text-neutral-700">
          {displayTitle}
        </h1>
        <div className="whitespace-pre-line text-base font-normal leading-[1.32] text-neutral-600">
          <p>{displayDescription}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Link {...destination} replace onClick={trackContinue}>
          <Button fullWidth>{displayButtonText}</Button>
        </Link>
      </div>
    </ImageDrawerFlowLayout>
  );
};

export const Route = createFileRoute("/flows/complete/$flowId")({
  component: FlowCompletePage,
});
